import { createApi } from '@reduxjs/toolkit/query/react'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { paginate } from '../utils'
import { EmailAction, EmailType, EmailTypeDetectionConfig, EmailTypeKind } from '../../models/EmailType'

const _BASE_URL = import.meta.env.REACT_APP_INTEGRATIONS_SERVICE_API_BASE_URL!

export interface GetEmailTypesRequest extends BasePageRequest {
  includeInactive?: boolean
  kind?: EmailTypeKind
}

export interface GetAllEmailTypesRequest extends BaseRequest {
  includeInactive?: boolean
  kind?: EmailTypeKind
}

export interface GetEmailTypeByIdRequest extends BaseRequest {
  emailTypeId: string
}

interface BaseCreateEmailTypePayload {
  name: string
  isActive?: boolean
  detectionConfig: EmailTypeDetectionConfig
}

interface CreateReplyEmailTypePayload extends BaseCreateEmailTypePayload {
  kind: EmailTypeKind.REPLY
  roleIds?: string[]
}

interface CreateNewEmailTypePayload extends BaseCreateEmailTypePayload {
  kind: EmailTypeKind.NEW
}

export type CreateEmailTypePayload = CreateReplyEmailTypePayload | CreateNewEmailTypePayload

export interface CreateEmailTypeRequest extends BaseRequest {
  payload: CreateEmailTypePayload
}

export interface UpdateEmailTypePayload {
  name?: string
  isActive?: boolean
  detectionConfig?: EmailTypeDetectionConfig
  roleIds?: string[]
}

export interface UpdateEmailTypeRequest extends BaseRequest {
  emailTypeId: string
  payload: UpdateEmailTypePayload
}

export interface DeleteEmailTypeRequest extends BaseRequest {
  emailTypeId: string
}

export type GetUncategorizedEmailActionRequest = BaseRequest

export interface SetUncategorizedEmailActionRequest extends BaseRequest {
  payload: {
    emailAction: EmailAction
  }
}

export interface GetUncategorizedEmailActionResponse extends BaseResponse<EmailAction> {
  emailAction: EmailAction
}

export const emailTypeApi = createApi({
  reducerPath: 'emailTypeApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['EmailType'],
  endpoints: (builder) => ({
    getEmailTypes: builder.query<BasePageResponse<EmailType[]>, GetEmailTypesRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/email-types`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            includeInactive: request.includeInactive,
            kind: request.kind,
          },
        }
      },
      providesTags: ['EmailType'],
    }),
    getAllEmailTypes: builder.query<BaseResponse<EmailType[]>, GetAllEmailTypesRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<EmailType>(
          {
            url: `/accounts/${request.accountId}/email-types`,
            params: {
              includeInactive: request.includeInactive,
              kind: request.kind,
            },
          },
          fetchWithBQ,
        )
      },
      providesTags: ['EmailType'],
    }),
    getEmailTypeById: builder.query<BaseResponse<EmailType>, GetEmailTypeByIdRequest>({
      query: (request) => `/accounts/${request.accountId}/email-types/${request.emailTypeId}`,
      providesTags: ['EmailType'],
    }),
    createEmailType: builder.mutation<EmailType, CreateEmailTypeRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/email-types/`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<EmailType>) => {
        return response.data
      },
      invalidatesTags: ['EmailType'],
    }),
    updateEmailType: builder.mutation<void, UpdateEmailTypeRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/email-types/${request.emailTypeId}`,
        method: 'PATCH',
        body: request.payload,
      }),
      invalidatesTags: ['EmailType'],
    }),
    deleteEmailType: builder.mutation<void, DeleteEmailTypeRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/email-types/${request.emailTypeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailType'],
    }),
    getUncategorizedEmailAction: builder.query<GetUncategorizedEmailActionResponse, GetUncategorizedEmailActionRequest>({
      query: (request) => `/accounts/${request.accountId}/email-types/uncategorized/action`,
      transformResponse: (response: BaseResponse<GetUncategorizedEmailActionResponse>) => {
        return response.data
      },
    }),
    setUncategorizedEmailAction: builder.mutation<void, SetUncategorizedEmailActionRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/email-types/uncategorized/action`,
        method: 'PUT',
        body: request.payload,
      }),
    }),
  }),
})

export const {
  useGetEmailTypesQuery,
  useLazyGetAllEmailTypesQuery,
  useGetAllEmailTypesQuery,
  useGetEmailTypeByIdQuery,
  useLazyGetEmailTypeByIdQuery,
  useCreateEmailTypeMutation,
  useUpdateEmailTypeMutation,
  useDeleteEmailTypeMutation,
  useGetUncategorizedEmailActionQuery,
  useSetUncategorizedEmailActionMutation,
} = emailTypeApi
